import {Component, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {ConfirmDirective} from "../../../../../../directives/confirm/confirm.directive";
import {IsGrantedDirective} from "../../../../../../directives/is-granted/is-granted.directive";
import {PaginatorComponent} from "../../../../../layout/paginator/paginator.component";
import {TableSorterHeaderDirective} from "../../../../../../directives/table-sorter/table-sorter-header.directive";
import {Paginator} from '../../../../../../tools/pagination/paginator';
import {TableSorter} from '../../../../../../tools/table-sorter/table-sorter';
import {LangService} from '../../../../../../services/language/lang.service';
import {DialogService} from '../../../../../../services/dialog/dialog.service';
import {LoaderService} from '../../../../../../services/loader/loader.service';
import {SnackbarService} from '../../../../../../services/snackbar/snackbar.service';
import {StaticPage} from '../../../../../../models/cms/static-page';
import {RouterLink} from '@angular/router';
import {FormsModule} from '@angular/forms';
import {Subject} from 'rxjs';
import {debounceTime} from 'rxjs/operators';
import {Language} from '../../../../../../models/language';
import {TableSorterDirective} from '../../../../../../directives/table-sorter/table-sorter.directive';
import {StaticPageService} from '../../../../../../services/cms/static-page.service';
import {DatePipe} from '@angular/common';
import {MetaTitle} from '../../../../../../decorator/meta-title';
import {StaticPageStatus} from "../../../../../../constants/static-page-status";
import {IsGranted} from '../../../../../../decorator/is-granted';
import {User} from '../../../../../../models/user';
import {AuthService} from '../../../../../../services/auth/auth.service';
import {ConfirmService} from '../../../../../../services/confirm/confirm.service';
import {ProductService} from '../../../../../../services/product/product.service';
import {Product} from '../../../../../../models/product';
import {PageType} from '../../../../../../models/page-type';
import {PageTypeService} from '../../../../../../services/page-type/page-type.service';
import {UserService} from '../../../../../../services/user/user.service';
import {DropdownSelectComponent} from '../../../../../layout/dropdown-select/dropdown-select.component';
import {
  DropdownSelectOptionComponent
} from '../../../../../layout/dropdown-select-option/dropdown-select-option.component';
import {StaticPageDuplicateComponent} from '../static-page-duplicate/static-page-duplicate.component';
import {ListFilters} from '../../../../../../tools/list-filters/list-filters';
import {ListFilterService} from '../../../../../../services/list-filter/list-filter.service';

@Component({
  selector: 'app-static-page-list',
  imports: [
    ConfirmDirective,
    IsGrantedDirective,
    PaginatorComponent,
    TableSorterHeaderDirective,
    RouterLink,
    FormsModule,
    TableSorterDirective,
    DatePipe,
    DropdownSelectComponent,
    DropdownSelectOptionComponent,
  ],
  templateUrl: './static-page-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './static-page-list.component.scss'
})
@IsGranted(['STATIC_PAGE_VIEW'])
@MetaTitle('Page Builder List')
export class StaticPageListComponent implements OnInit {
  connectedUser!: User;
  staticPages: StaticPage[] = [];
  products: Product[] = [];
  languages: Language[] = [];
  pageTypes: PageType[] = [];
  users: User[] = [];

  paginator: Paginator = new Paginator();
  tableSorter: TableSorter = new TableSorter('id', 'asc');

  filters: ListFilters;

  private nameChange$ = new Subject<void>();

  protected readonly StaticPageStatus = StaticPageStatus;

  constructor(
    protected langService: LangService,
    private auth: AuthService,
    private dialog: DialogService,
    private loader: LoaderService,
    private confirm: ConfirmService,
    private snackbar: SnackbarService,
    private staticPageService: StaticPageService,
    private productService: ProductService,
    private pageTypeService: PageTypeService,
    private userService: UserService,
    private listFilterService: ListFilterService,
  ) {
    this.filters = listFilterService.createFilters({
      name: 'string',
      productId: 'number[]',
      lang: 'string[]',
      pageTypeId: 'number[]',
      userId: 'number[]',
    }, this.paginator, this.tableSorter);
  }

  ngOnInit(): void {
    this.auth.getConnectedUser().subscribe((user: User) => {
      this.connectedUser = user;
    });

    this.productService.findAll({sort: 'name'}).subscribe((products: Product[]) => {
      this.products = products;
    });

    this.langService.getLanguages().subscribe((languages: Language[]) => {
      this.languages = languages;
    });

    this.pageTypeService.findAll({sort: 'name'}).subscribe((pageTypes: PageType[]) => {
      this.pageTypes = pageTypes;
    });

    this.userService.findAll({sort: 'firstname,lastname'}).subscribe((users: User[]) => {
      this.users = users;
    });

    this.nameChange$.pipe(debounceTime(300)).subscribe(() => this.filters.onFilterChange());

    this.filters.init(() => this.fetchAll());
  }

  onNameChange(): void {
    this.nameChange$.next();
  }

  fetchAll(): void {
    const params: any = {...this.filters.toParams()};

    this.loader.startLoading();
    this.staticPageService.findAll(params, this.paginator, this.tableSorter).subscribe((staticPages: StaticPage[]) => {
      this.staticPages = staticPages;
      this.loader.stopLoading();
    });
  }

  delete(staticPage: StaticPage): void {
    if (staticPage.id) {
      this.loader.startLoading();
      this.staticPageService.delete(staticPage.id).subscribe(() => {
        this.loader.stopLoading();
        this.snackbar.success('The page has been deleted');
        this.fetchAll();
      });
    }
  }

  ownsStaticPage(staticPage: StaticPage): boolean {
    return staticPage.user?.id === this.connectedUser?.id;
  }

  duplicate(staticPage: StaticPage): void {
    this.dialog.openDialog(StaticPageDuplicateComponent, {
      data: {staticPage},
      backdrop: false,
    }).subscribe(() => {
      this.fetchAll();
    });
  }

  confirmDelete(staticPage: StaticPage): void {
    this.confirm.open('This is a public page! Are you sure about deleting this page?').subscribe((result: boolean) => {
      if (result) {
        this.delete(staticPage);
      }
    })
  }
}
